import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  exportContacts,
  fetchContacts,
  saveNote,
  type Contact,
} from "../contacts/contacts";
import { ApiError } from "../api/client";
import { useEvent } from "../context/EventProvider";
import { useUnsavedGuard } from "../funnel/leaveGuard";
import { useParticipation } from "../funnel/useParticipation";
import { fallbackOf } from "../settings/catalog";
import {
  useFlagOr,
  useLocale,
  useSettings,
  useT,
  useText,
} from "../settings/context";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Prose } from "../ui/Prose";
import { useNavigate } from "react-router-dom";
import { pageOf, pagePath } from "../pages/registry";
import { isClosed } from "../pages/closedPages";
import { withContext } from "./registry";
import { InPlace } from "../panel/InPlace";

/**
 * ═══ LE CARNET DE RENCONTRES ═══
 *
 * Les badges que l'on a scannés pendant l'événement, avec ce qu'ils révèlent et la note
 * qu'on a prise sur chacun.
 *
 * ⚠️ CE QUE MONTRE UNE FICHE EST DÉCIDÉ AU BACK OFFICE, et résolu par le SERVEUR — voir
 * `contacts/contacts.ts`. Cet écran affiche ce qu'on lui donne, dans l'ordre où on le lui
 * donne. C'est la question la plus sensible de la page — ce qu'un badge révèle de
 * quelqu'un — et elle n'a qu'une réponse.
 *
 * ⚠️ LE BOUTON MÈNE À LA PAGE DE SCAN, ET SON ADRESSE VIENT DU REGISTRE. C'est la même
 * que celle du menu : deux façons d'écrire une adresse, c'est un bouton qui pointe à côté
 * de sa page. Il ne s'affiche que si cette page est déclarée — elle l'est, mais la garde
 * coûte une ligne et fait que retirer l'écran retire le bouton avec lui.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback: what the site says of itself follows the language being read.
 */
/** La page de scan, prise au registre — jamais son adresse retapée. */
const SCAN = pageOf("scan");

/**
 * À partir de combien de fiches la recherche apparaît.
 *
 * Sous ce nombre elle est du bruit : on voit toutes ses fiches d'un coup d'œil. Au-dessus,
 * faire défiler pour retrouver quelqu'un dont on a le nom en tête devient le geste le
 * plus long de la page.
 */
const SEARCH_THRESHOLD = 8;

export function Contacts() {
  const t = useT();
  const event = useEvent();
  const { me, unavailable } = useParticipation();
  const client = useQueryClient();
  const navigate = useNavigate();
  const locale = useLocale();
  const { value } = useSettings();
  /* Fermée par l'organisateur, la page de scan ne s'offre plus : le bouton mènerait à
     la page introuvable. Voir `closedKey`. */
  const scanOpen = !!SCAN && !isClosed(SCAN, value);

  const title = useText(
    "FO_MEETING_V2_CT_TITLE",
    fallbackOf("FO_MEETING_V2_CT_TITLE"),
  );
  const subtitle = useText(
    "FO_MEETING_V2_CT_SUBTITLE",
    fallbackOf("FO_MEETING_V2_CT_SUBTITLE"),
  );
  const html = useText("FO_MEETING_V2_CT_HTML");
  const noteShown = useFlagOr("FO_MEETING_V2_CT_COMMENT");
  const exportShown = useFlagOr("FO_MEETING_V2_CT_EXPORT");
  const exportLabel = useText(
    "FO_MEETING_V2_CT_EXPORT_LABEL",
    fallbackOf("FO_MEETING_V2_CT_EXPORT_LABEL"),
  );

  const q = useQuery({
    queryKey: ["contacts", event?.domainId, event?.eventId, me?.id],
    queryFn: ({ signal }) =>
      fetchContacts(event!.domainId, event!.eventId, signal),
    enabled: !!event && typeof me?.id === "number",
    retry: false,
  });
  const contacts = q.data ?? [];

  /**
   * ⚠️ CE QUI EST TAPÉ, PAR-DESSUS CE QUE LE SERVEUR TIENT — dérivé, jamais semé. Les
   * fiches arrivent par une requête, et un `useState` initialisé avec elles s'exécute
   * AVANT : les zones de note seraient restées vides sur des notes qui existent. Même
   * règle qu'à l'étape informations.
   */
  const [tape, setTape] = useState<Record<number, string>>({});
  const noteDe = (c: Contact) => (c.id in tape ? tape[c.id]! : c.note);

  /**
   * ⚠️ UNE NOTE TAPÉE NE PART PAS SANS UN MOT. Seul le bouton enregistre (voir la zone
   * de texte) : recharger l'onglet ou suivre le menu jetait ce qu'on venait d'écrire,
   * sans rien dire. Même règle que le marqueur « note modifiée » de la fiche repliée,
   * et mêmes portes que le parcours d'inscription (`funnel/leaveGuard.ts`). Seul un
   * changement de page compte : la fiche reste montée tant qu'on y est.
   */
  const unsavedNote = contacts.some((c) => noteDe(c) !== c.note);
  useUnsavedGuard(
    unsavedNote,
    ({ currentLocation, nextLocation }) =>
      unsavedNote && nextLocation.pathname !== currentLocation.pathname,
  );

  /** La fiche ouverte — une seule, voir le commentaire de l'en-tête. */
  const [openId, setOpenId] = useState<number | null>(null);
  const [search, setSearch] = useState("");

  /* ⚠️ LA RECHERCHE PORTE AUSSI SUR LES CHAMPS RÉVÉLÉS. On se souvient rarement du nom
     seul — « la personne d'Atelier Nord », « celle du service qualité ». Sans accents ni
     casse : on tape vite, debout, d'une main. */
  const withoutAccent = (t: string) =>
    t
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  const q0 = withoutAccent(search.trim());
  const found = q0
    ? contacts.filter((c) =>
        withoutAccent([c.name, ...c.fiche.map((f) => f.value)].join(" ")).includes(
          q0,
        ),
      )
    : contacts;

  const enCours = (c: Contact) =>
    saved.isPending && saved.variables?.id === c.id;

  /**
   * ⚠️ UNE SEULE ÉCRITURE PAR ENREGISTREMENT, ET UN DRAPEAU D'ÉTAT NE SUFFIT PAS.
   * `isPending` n'arrive qu'avec un rendu : un second clic rapproché peut encore le lire
   * à `false` et envoyer sa propre requête. Une référence, elle, change tout de suite.
   */
  const enVol = useRef(false);
  const onSaveNote = (c: Contact) => {
    if (enVol.current) return;
    enVol.current = true;
    saved.mutate(c, {
      onSettled: () => {
        enVol.current = false;
      },
    });
  };

  const saved = useMutation({
    mutationFn: (c: Contact) =>
      saveNote(event!.domainId, event!.eventId, me!.id, c.id, noteDe(c)),
    /* Ce qu'on vient d'écrire EST ce que le serveur tient : on le relit, sinon revenir
       sur la page rendrait la note d'avant. */
    onSuccess: (_data, c) => {
      /* La relecture TRIM la note : une espace finale laissait « pas encore
         enregistrée » à vie. Ce qui est enregistré n'est plus « tapé ». */
      setTape((prev) => {
        const next = { ...prev };
        delete next[c.id];
        return next;
      });
      void client.invalidateQueries({ queryKey: ["contacts"] });
    },
  });

  /**
   * ⚠️ LE TABLEUR S'OUVRE, IL NE SE TÉLÉCHARGE PAS ICI. Le serveur écrit le classeur sur
   * S3 et rend un lien SIGNÉ : c'est lui qui porte le nom du fichier et son type. Le
   * forcer dans un `<a download>` comme le badge n'apporterait rien — l'adresse n'est
   * pas un blob local, et le navigateur sait déjà quoi en faire.
   */
  const exported = useMutation({
    mutationFn: () => exportContacts(event!.domainId, event!.eventId),
    onSuccess: (url) => window.open(url, "_blank", "noopener"),
  });

  const when = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    try {
      return new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      }).format(d);
    } catch {
      return d.toLocaleString();
    }
  };

  /** Les initiales de l'avatar — deux lettres, prises sur les deux premiers mots. */
  const initials = (name: string) =>
    name
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word[0] ?? "")
      .join("")
      .toUpperCase() || "?";

  return (
    <section className="page">
      <div className="ttl" data-cfg="ctTitle">
        <InPlace settingKey="FO_MEETING_V2_CT_TITLE" text={title} as="h2" />
        <InPlace settingKey="FO_MEETING_V2_CT_SUBTITLE" text={subtitle} as="p" />
      </div>

      {/* Le bloc libre de l'organisateur, sous le titre : son HTML, assaini comme
          partout (`ui/Prose`). Vide, rien ne s'affiche. */}
      {html.trim() !== "" && (
        <Prose html={html} className="se-prose" data-cfg="ctTitle" />
      )}

      {q.isPending && !unavailable && (
        <p className="empty">
          <span className="spin" aria-hidden="true" />
          <span className="a11y" role="status">
            {t("LOADING_YOUR_CONTACTS", 'Chargement de votre carnet')}
          </span>
        </p>
      )}

      {/* `unavailable` : l'identité n'a pas pu être lue (échec non-401), la requête reste
          désactivée et `isPending` sans fin. On le dit plutôt que de tourner à vide. */}
      {(q.isError || unavailable) && (
        <p className="loginerr" role="alert">
          {t("CONTACTS_NOT_LOADED", "Votre carnet n'a pas pu être chargé. Réessayez dans un instant.")}
        </p>
      )}

      {/* ⚠️ LA RECHERCHE N'APPARAÎT QU'À PARTIR D'UN CERTAIN NOMBRE. Sur trois fiches
          elle est du bruit — on les voit toutes. Sur quarante, faire défiler pour
          retrouver quelqu'un dont on a le nom en tête est le geste le plus long de la
          page. Elle cherche AUSSI dans les champs révélés : « la personne d'Atelier
          Nord » est souvent tout ce dont on se souvient. */}
      {contacts.length >= SEARCH_THRESHOLD && (
        <div className="f">
          <label htmlFor="ct-q">{t("SEARCH", "Rechercher")}</label>
          <input
            id="ct-q"
            type="search"
            value={search}
            autoComplete="off"
            placeholder={t("SEARCH_CONTACTS_PLACEHOLDER", "Un nom, une société")}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      {contacts.length > 0 && (
        <p className="hint" role="status">
          {found.length !== contacts.length
            ? t("N_OF_M", "$1 sur $2", [found.length, contacts.length])
            : contacts.length > 1
              ? t("N_CONTACTS_MANY", "$1 contacts", [contacts.length])
              : t("N_CONTACTS_ONE", "$1 contact", [contacts.length])}
        </p>
      )}

      <div className="clist" data-cfg="ctFiche">
        {found.map((contact) => {
          const isOpen = openId === contact.id;
          return (
            <div className={`ctc${isOpen ? " ctc--on" : ""}`} key={contact.id}>
              {/* ⚠️ L'EN-TÊTE EST LE BOUTON, et la fiche s'ouvre dessous. Tout déplié,
                  huit contacts faisaient deux mille pixels ; à quarante, la page devient
                  impraticable. Replié, chacun tient sur une ligne.

                  ⚠️ ET UNE SEULE À LA FOIS. Ce n'est pas de la mise en page : la NOTE
                  est privée, et dans un salon on lit par-dessus l'épaule. Ouvertes
                  toutes, elles s'affichaient toutes — ouvrir une fiche referme la
                  précédente, et refermer n'en laisse aucune à l'écran. */}
              <button
                type="button"
                className="ctc__h"
                aria-expanded={isOpen}
                onClick={() => setOpenId(isOpen ? null : contact.id)}
              >
                <span className="ctc__av" aria-hidden="true">
                  {initials(contact.name)}
                </span>
                {/* ⚠️ CIBLÉ PAR SA CLASSE, ET NON PAR `.ctc__h > span` : l'avatar est un
                    span lui aussi, et un `flex: 1` générique l'étirait en ellipse — la
                    maquette porte la trace de cette erreur, capture à l'appui. */}
                <span className="ctc__id">
                  <b>{contact.label}</b>
                  {contact.when && (
                    <small>{t("SCANNED_WHEN", "Scanné $1", [when(contact.when)])}</small>
                  )}
                </span>
                {/* ⚠️ « IL Y A UNE NOTE », SANS LA MONTRER. Savoir qu'on a déjà écrit
                    quelque chose évite de rouvrir chaque fiche une par une ; en révéler
                    un mot annulerait ce qu'on vient de refermer. */}
                {/* ⚠️ DEUX MARQUEURS, ET LA DIFFÉRENCE COMPTE. Depuis que rien ne
                    s'enregistre tout seul, ce qu'on a tapé peut attendre : replier la
                    fiche ne le perd pas, mais rien ne le rappelait non plus. Le second
                    marqueur le dit, sans montrer un mot de la note. */}
                {noteShown && noteDe(contact) !== contact.note && (
                  <span
                    className="ctc__note ctc__note--wip"
                    title={t("NOTE_EDITED_NOT_SAVED", "Note modifiée, pas encore enregistrée")}
                  >
                    <Icon name="fichier" />
                  </span>
                )}
                {noteShown &&
                  noteDe(contact) === contact.note &&
                  contact.note.trim() !== "" && (
                    <span className="ctc__note" title={t("YOU_TOOK_A_NOTE", "Vous avez pris une note")}>
                      <Icon name="fichier" />
                    </span>
                  )}
                <span className="ctc__x" aria-hidden="true">
                  <Icon name="chevron" />
                </span>
              </button>

              {isOpen && (
                <div className="ctc__b">
                  {contact.fiche.length > 0 && (
                    <div className="ctc__l">
                      {contact.fiche.map((field) => (
                        <span key={field.key}>
                          <i>{field.label} :</i> {field.value}
                        </span>
                      ))}
                    </div>
                  )}

                  {noteShown && (
                    <div className="f">
                      <label htmlFor={`note-${contact.id}`}>{t("YOUR_NOTE", 'Votre note')}</label>
                      <textarea
                        id={`note-${contact.id}`}
                        value={noteDe(contact)}
                        placeholder={t("NOTE_PLACEHOLDER", "Un mot pour vous souvenir de cette rencontre")}
                        onChange={(e) =>
                          setTape((t) => ({
                            ...t,
                            [contact.id]: e.target.value,
                          }))
                        }
                        /* ⚠️ RIEN NE S'ENREGISTRE TOUT SEUL. Quitter le champ le faisait,
                           et c'était le filet — mais un filet qui écrit : on effleure une
                           note, on passe à autre chose, et elle est modifiée sans qu'on
                           l'ait demandé. Sur une note personnelle prise pendant un salon,
                           ce n'est pas rattrapable, on ne sait même pas ce qu'il y avait
                           avant. Le bouton est le seul chemin (demandé le 2026-08-05). */
                      />

                      {/**
                       * ⚠️ LE GESTE SE VOIT, SINON IL N'EXISTE PAS. La note s'enregistrait
                       * en quittant le champ, et RIEN ne le disait : on ne savait ni
                       * comment enregistrer, ni si ça l'avait été. Question posée
                       * telle quelle le 2026-08-05.
                       *
                       * ⚠️ ET LE BOUTON RESTE PENDANT L'ÉCRITURE. Il tourne tant qu'elle
                       * dure, puis cède la place au mot qui confirme : disparaître au
                       * clic laisserait croire que rien n'est parti.
                       */}
                      {noteDe(contact) !== contact.note || enCours(contact) ? (
                        <div className="ctc__save">
                          <Button
                            type="button"
                            loading={enCours(contact)}
                            disabled={saved.isPending}
                            onClick={() => onSaveNote(contact)}
                          >
                            {t("SAVE_NOTE", "Enregistrer la note")}
                          </Button>
                        </div>
                      ) : (
                        saved.isSuccess &&
                        saved.variables?.id === contact.id && (
                          <p className="ctc__ok" role="status">
                            {t("NOTE_SAVED", 'Note enregistrée')}
                          </p>
                        )
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Une recherche qui ne rend rien se dit, sinon on croit son carnet vidé. */}
      {contacts.length > 0 && found.length === 0 && (
        <p className="empty">
          {t("NO_CONTACT_MATCHES", "Aucun contact ne correspond à « $1 ».", [
            search,
          ])}
        </p>
      )}

      {/* ⚠️ LE VIDE SE DIT, ET IL DIT COMMENT LE REMPLIR. Un carnet vide est l'état
          NORMAL au premier jour : sans un mot, la page se lit comme une panne. */}
      {!q.isPending && !q.isError && contacts.length === 0 && (
        <p className="empty">
          {t(
            "CONTACTS_EMPTY",
            "Votre carnet est vide. Scannez le badge des personnes que vous rencontrez pour les retrouver ici.",
          )}
        </p>
      )}

      {saved.isError && (
        <p className="hint" role="alert">
          {t("NOTE_NOT_SAVED", "Votre note n'a pas pu être enregistrée. Réessayez.")}
        </p>
      )}
      {exported.isError && (
        <p className="hint" role="alert">
          {/* La raison n'est donnée que si le SERVEUR l'a dite. L'adresse manquante
              lève une phrase française en dur (`exportContacts`), qui s'affichait
              brute sur le site anglais, et deux fois la même en français (bug #489). */}
          {exported.error instanceof ApiError
            ? t("EXPORT_NOT_PREPARED", "L'export n'a pas pu être préparé : $1", [
                exported.error.message,
              ])
            : t("EXPORT_NOT_PREPARED_PLAIN", "L'export n'a pas pu être préparé.")}
        </p>
      )}

      {/* ⚠️ LES DEUX DANS LA MÊME RANGÉE, et le scan en dernier : c'est le geste qu'on
          vient faire ici, l'export est ce qu'on emporte à la fin. La maquette les range
          ainsi, et sa règle vaut — un seul bouton plein, le dernier. */}
      {(scanOpen || (exportShown && contacts.length > 0)) && (
        <div className="actions">
          {exportShown && contacts.length > 0 && (
            <Button
              type="button"
              variant={scanOpen ? "ghost" : undefined}
              loading={exported.isPending}
              onClick={() => exported.mutate()}
            >
              {exportLabel}
            </Button>
          )}
          {scanOpen && SCAN && (
            <Button
              type="button"
              onClick={() =>
                navigate(withContext(pagePath(SCAN), event?.token))
              }
            >
              {t("SCAN_A_BADGE", "Scanner un badge")}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
